/**
 * Where a dragged card would land inside a stage column (SE-6554 item N).
 *
 * ## Why this exists
 *
 * The board previously derived its insertion slot from `over.rect` — the rect of
 * the COLUMN droppable, not of the card being dragged:
 *
 * ```
 * const dropY = over.rect.top + over.rect.height / 2;
 * const containerTop = over.rect.top - (players.length * 120) / 2;
 * index = floor((dropY - containerTop) / 120);
 * ```
 *
 * Every term there is a property of the column, so the result is a CONSTANT for
 * a given column and card count. It does not move with the pointer. Two visible
 * symptoms followed: the insertion indicator sat at a fixed slot no matter where
 * you aimed, and the drop persisted at that same fixed slot — the card landed
 * somewhere other than where the user pointed. That is a large part of what
 * reads as "the drag is dead".
 *
 * ## What it does instead
 *
 * Measures the ACTUAL rendered rows and compares their midpoints against the
 * vertical centre of the DRAGGED rect (`active.rect.current.translated`, which
 * dnd-kit updates every move). The card lands before the first row whose middle
 * sits below the dragged card's middle — the standard, predictable rule.
 *
 * ## One index, two consumers
 *
 * `KanbanColumnContentContainer` calls this to position the live drop gap, and
 * `MainContainer` calls it again on drag end to compute the index it persists.
 * They MUST agree: the gap is the promise, the drop is the delivery. Sharing one
 * function is what makes the card land exactly in the gap that opened for it —
 * with no reflow at the moment of drop, because the gap is already the card's
 * height at the card's index.
 *
 * ## Measurement source
 *
 * The rows are addressed by the `columnCardFor(columnId, playerId)` test id they
 * already carry for the SE-5491 dual-view check — a stable public identity, so
 * this never depends on the column's internal markup. Rows below the lazy-mount
 * fold (SE-5492) have no element; they are necessarily below every mounted row,
 * so leaving them unmeasured yields the same answer as measuring them would.
 */

import type { ColumnData } from "../types/types";

import { TEST_ID_PLAYER_KANBAN } from "e2e/testId";

/**
 * The minimum of a column this needs: its identity and its rendered order. Kept
 * structural rather than the whole `ColumnData` so a caller holding the column
 * in pieces — the lane container has exactly `id` + `players` — does not have to
 * fabricate title/count fields that play no part in the geometry.
 */
export type ColumnInsertionTarget = Pick<ColumnData, "id" | "players">;

/** The minimum a caller needs from a dnd-kit `ClientRect` to place a card. */
export interface DraggedRect {
  readonly top: number;
  readonly height: number;
}

/**
 * Read the viewport rect of one rendered card row, or null when that row is not
 * mounted (below the SE-5492 lazy-mount fold) or the platform has no DOM.
 * @param columnId - The stage column the row belongs to
 * @param playerId - The player whose row to measure
 * @returns The row's top/height, or null when it cannot be measured
 */
const readRowRect = (
  columnId: string,
  playerId: string
): DraggedRect | null => {
  if (typeof document === "undefined") return null;
  const selector = `[data-testid="${TEST_ID_PLAYER_KANBAN.columnCardFor(
    columnId,
    playerId
  )}"]`;
  const node = document.querySelector(selector);
  if (!node?.getBoundingClientRect) return null;
  const { top, height } = node.getBoundingClientRect();
  return { top, height };
};

/**
 * The slot a dragged card would occupy in `column`, as an index into
 * `column.players`.
 *
 * The returned index is measured against the column AS RENDERED, so a caller
 * that persists it must first translate it to a full-board index with
 * `toFullBoardIndex` — exactly as the previous coordinate-derived index was
 * translated (SE-6488).
 * @param column - The column being dropped into, as rendered
 * @param draggedRect - The dragged card's CURRENT rect (`active.rect.current.translated`)
 * @returns A slot in `0..column.players.length`; 0 for an empty or unmeasurable column
 */
export const resolveColumnInsertionIndex = (
  column: ColumnInsertionTarget | undefined,
  draggedRect: DraggedRect | null | undefined
): number => {
  const players = column?.players ?? [];
  if (players.length === 0) return 0;
  if (!draggedRect) return players.length;

  const draggedMiddle = draggedRect.top + draggedRect.height / 2;
  const measured = players
    .map(player => readRowRect(column?.id ?? "", player.id))
    .filter((rect): rect is DraggedRect => rect !== null);

  // Nothing measurable in a column that HAS cards means there is no DOM to
  // measure at all — a native renderer, or a test environment with no layout.
  // That is a different situation from "some rows are below the lazy-mount
  // fold", and it gets the same answer as a missing dragged rect: append. The
  // two no-measurement paths must agree, or the same unmeasurable board would
  // prepend down one branch and append down the other.
  if (measured.length === 0) return players.length;

  // Count the rows sitting ABOVE the dragged card. A row past the lazy-mount
  // fold (SE-5492) has no element and is necessarily below every mounted row,
  // so leaving it out of the count gives the same answer as measuring it would.
  return measured.filter(rect => rect.top + rect.height / 2 < draggedMiddle)
    .length;
};
